import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { colors } from "../../Assets/styles/colors";
import moment from "moment";
import { useTranslate } from '@tolgee/react';
import { Repeat } from "lucide-react";

const MeetingListElement = ({
  data,
  selected,
  handleJoinMeeting,
  handleSelectMeeting,
}) => {
  const [ongoing, setOngoing] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [joinVisible, setJoinVisible] = useState(false);
  const { t } = useTranslate();

  const title = data.title;
  const description = data.description;

  const checkOngoing = (startTime) => {
    const currentTime = moment();
    const startAtThreshold = moment(startTime).subtract(5, "minutes"); // 5 min before start

    setOngoing(currentTime.isAfter(startAtThreshold));
    setIsStarted(currentTime.isAfter(startTime));

    setJoinVisible(currentTime.isAfter(startAtThreshold)); // Visible from 5 minutes before onwards
  };

  const handleJoinMeetingNoPropagation = (e) => {
    e.stopPropagation();
    handleJoinMeeting(data);
  };

  useEffect(() => {
    checkOngoing(data.start_at);

    // Set up an interval to re-check every minute for real-time updates
    const interval = setInterval(() => {
      checkOngoing(data.start_at);
    }, 60000);

    return () => clearInterval(interval);
  }, [data]);

  return (
    <MeetingELementContainer
      selected={selected}
      ongoing={ongoing}
      onClick={(e) => {
        handleSelectMeeting(data);
      }}
    >
      <MeetingDetails>
        <Line>
          {data.recurring_meeting_series_id && <RecurringIcon size={13} />}
          <MeetingTitle>{title}</MeetingTitle>{" "}
          {joinVisible ? (
            <JoinCell onClick={(e) => handleJoinMeetingNoPropagation(e)}>
              {" "}
              {t('meetings.join_now')}
            </JoinCell>
          ) : (
            <TimeCell>{moment(data.start_at).format("HH:mm")}</TimeCell>
          )}
        </Line>
        <Line>
          <MeetingDescription>{description} </MeetingDescription>
          <DateCell>
            {ongoing
              ? !isStarted
                ? // Show current time (e.g., "15:30") if ongoing and started
                moment(data.start_at).format("HH:mm")
                : // Show "Ongoing" text if ongoing but not yet started
                t('call_status.ongoing')
              : // Show formatted date if not ongoing
              moment(data.start_at).format("DD.MM.YYYY")}
          </DateCell>
        </Line>
      </MeetingDetails>
    </MeetingELementContainer>
  );
};

export default MeetingListElement;

const MeetingELementContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  align-items: center;
  padding: 13px 16px;
  background: ${({ selected, ongoing, theme }) =>
    selected
      ? theme.chat.selectedBackground
      : ongoing
        ? theme.meetingComponent.ongoingMeetingElementBackground
        : theme.menu.background};
  &:hover {
    background: ${({ theme }) => theme.chat.selectedBackground};
  }
`;

const RecurringIcon = styled(Repeat)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.menu.selectedTextColor};
`;

const MeetingDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 5px;
`;
const MeetingTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  display: -webkit-box;
  -webkit-line-clamp: 1; /* Limit to 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const MeetingDescription = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  display: -webkit-box;
  -webkit-line-clamp: 1; /* Limit to 2 lines */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const TimeCell = styled.p`
  margin-left: auto;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  text-align: left;
`;

const DateCell = styled.p`
  margin-left: auto;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  text-align: left;
`;

const JoinCell = styled.p`
  margin-left: auto;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  text-align: left;
  background-color: ${({ theme }) =>
    theme.meetingComponent.meetingJoinBackground};
  padding: 2px 10px;
  border-radius: 8px; /* Added corner radius */

  white-space: nowrap; /* Prevents text from wrapping */
  flex-shrink: 0; /* Prevents shrinking */
`;
